import React, { useEffect, useState } from "react";
import { Flame, Calendar, Trophy, Sparkles, TrendingUp, Activity } from "lucide-react";
import { endpoints } from "../lib/api";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function motivation(stats) {
  if (stats.streak >= 7) return "Unstoppable. Keep the streak alive! 🔥";
  if (stats.streak >= 3) return "You're on a roll - don't break the chain.";
  if (stats.week_count >= 3) return "Great momentum this week 💪";
  if (stats.sessions_total === 0) return "First check-in unlocks the streak 🌱";
  if (stats.best_streak > stats.streak) return `Close to your best (${stats.best_streak} days). Go get it.`;
  return "Consistency beats intensity - show up tomorrow.";
}

export default function ProgressCard({ memberId, compact = false, portalStats = null }) {
  const [stats, setStats] = useState(portalStats);
  const [loading, setLoading] = useState(!portalStats);

  useEffect(() => {
    if (portalStats) { setStats(portalStats); return; }
    if (!memberId) return;
    endpoints.memberStats(memberId)
      .then(r => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [memberId, portalStats]);

  if (loading) {
    return (
      <div className="border border-border rounded-xl p-5 bg-card mb-6" data-testid="progress-card-loading">
        <div className="h-28 animate-pulse bg-muted/40 rounded-lg" />
      </div>
    );
  }
  if (!stats) return null;

  const maxWeekdayVal = Math.max(1, ...stats.weekday_counts);

  return (
    <div
      className={`border border-border rounded-xl p-5 bg-card ${compact ? "" : "mb-6"}`}
      data-testid="progress-card"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2">
          <Activity className="w-4 h-4 text-muted-foreground" /> Progress
        </h3>
        <p className="text-xs text-muted-foreground italic" data-testid="progress-motivation">
          {motivation(stats)}
        </p>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Tile
          icon={Flame}
          iconTone="text-orange-500"
          label="Current streak"
          value={<><span>{stats.streak}</span><span className="text-sm font-normal text-muted-foreground ml-1">day{stats.streak === 1 ? "" : "s"}</span></>}
          testid="tile-streak"
        />
        <Tile
          icon={Trophy}
          iconTone="text-amber-500"
          label="Best streak"
          value={<><span>{stats.best_streak}</span><span className="text-sm font-normal text-muted-foreground ml-1">day{stats.best_streak === 1 ? "" : "s"}</span></>}
          testid="tile-best-streak"
        />
        <Tile
          icon={Calendar}
          iconTone="text-emerald-500"
          label="This week"
          value={<><span>{stats.week_count}</span><span className="text-sm font-normal text-muted-foreground ml-1">session{stats.week_count === 1 ? "" : "s"}</span></>}
          testid="tile-week-count"
        />
        <Tile
          icon={TrendingUp}
          iconTone="text-indigo-500"
          label="Total sessions"
          value={stats.sessions_total}
          testid="tile-sessions-total"
        />
      </div>

      {/* Weekday breakdown + Membership status row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Last 7 days activity
          </p>
          <div className="flex items-end gap-1.5 h-24" data-testid="weekday-breakdown">
            {stats.weekday_counts.map((c, i) => {
              const h = (c / maxWeekdayVal) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group" title={`${WEEKDAYS[i]}: ${c}`}>
                  <span className="text-[10px] font-semibold text-foreground/80 tabular-nums h-3">
                    {c > 0 ? c : ""}
                  </span>
                  <div className="w-full relative flex-1 bg-muted/40 rounded min-h-[4px]">
                    <div
                      className={`absolute bottom-0 left-0 right-0 rounded transition-all ${
                        c > 0 ? "bg-primary" : ""
                      }`}
                      style={{ height: c === 0 ? "2px" : `${Math.max(h, 18)}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-muted-foreground/80 font-medium">{WEEKDAYS[i][0]}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Membership</p>
          <div className="border border-border rounded-lg p-3 flex items-center justify-between">
            <div>
              <p className="font-display text-xl font-bold text-foreground">
                {stats.days_left != null ? `${stats.days_left} day${stats.days_left === 1 ? "" : "s"}` : "-"}
              </p>
              <p className="text-xs text-muted-foreground">
                {stats.days_left == null
                  ? "No expiry set"
                  : stats.days_left < 0
                  ? "Membership expired"
                  : stats.days_left <= 7
                  ? "Expiring soon"
                  : "remaining"}
              </p>
            </div>
            {stats.days_left != null && stats.days_left <= 7 && (
              <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                <Sparkles className="w-3 h-3" /> Renew
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, iconTone, label, value, testid }) {
  return (
    <div className="border border-border rounded-lg p-3 bg-background/30" data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-3.5 h-3.5 ${iconTone}`} />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
      <p className="font-display text-2xl font-bold text-foreground leading-none">
        {value}
      </p>
    </div>
  );
}
